import { useEffect } from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { Sparkles } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { PageProps, SeoAuditReport } from "@/types/inertia"

type EvidenceQuery = { query: string; impressions?: number; best_position?: number | null }

type Suggestion = {
  id: number
  brand: string
  brand_name: string
  page_label: string
  section_label: string | null
  level: string | null
  summary: string
  rationale: string | null
  target_phrases: string[]
  evidence: { queries?: EvidenceQuery[]; source?: string }
  status: "pending" | "accepted"
  created_at: string
}

type SearchTermsSummary = {
  last_pull_week: string | null
  gsc_configured: boolean
  gsc_terms: number
  autocomplete_terms: number
}

type AgentRun = {
  id: number
  status: "queued" | "running" | "succeeded" | "failed"
  trigger: "manual" | "scheduled"
  started_by: string | null
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
  exit_code: number | null
  output: string | null
}

type Agent = {
  configured: boolean
  current: AgentRun | null
  recent: AgentRun[]
}

type Props = PageProps & {
  latest_report: SeoAuditReport | null
  recent_reports: SeoAuditReport[]
  suggestions: Suggestion[]
  search_terms_summary: SearchTermsSummary
  agent: Agent
}

function when(iso: string | null) {
  return iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"
}

function duration(seconds: number | null) {
  if (seconds === null) return ""
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m ? `${m}m ${s}s` : `${s}s`
}

// The agent's state: what is running, and the last few outcomes. While a
// run is in flight the page re-reads just this prop every ten seconds —
// an Inertia partial reload, not a JSON endpoint.
function AgentPanel({ agent }: { agent: Agent }) {
  const running = agent.current !== null
  useEffect(() => {
    if (!running) return
    const timer = window.setInterval(() => router.reload({ only: ["agent", "suggestions"] }), 10_000)
    return () => window.clearInterval(timer)
  }, [running])

  return (
    <div className="mt-8 rounded-lg border border-hairline bg-surface">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline px-5 py-3">
        <div>
          <h2>SEO agent</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Pulls this week's search demand, studies the pages against it and the competitors, and files
            wording suggestions above. Runs every Monday; the button runs it now.
          </p>
        </div>
        {agent.current ? (
          <Badge tone="accent">
            {agent.current.status === "queued" ? "Queued" : "Running"} · since {when(agent.current.started_at)}
          </Badge>
        ) : agent.configured ? (
          <Badge tone="neutral">Idle</Badge>
        ) : (
          <Badge tone="neutral">Not configured</Badge>
        )}
      </div>
      {agent.recent.length > 0 && (
        <ul className="divide-y divide-hairline">
          {agent.recent.map((run) => (
            <li key={run.id} className="px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={run.status === "succeeded" ? "accent" : "signal"}>{run.status}</Badge>
                <span className="text-ink-body">{when(run.started_at)}</span>
                <span className="text-ink-muted">
                  · {run.trigger === "manual" ? `run by ${run.started_by ?? "an admin"}` : "scheduled"}
                  {run.duration_seconds !== null && ` · ${duration(run.duration_seconds)}`}
                </span>
              </div>
              {run.status === "failed" && run.output && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-ink-muted">What it said</summary>
                  <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-page p-3 font-mono text-xs leading-relaxed text-ink-body">
                    {run.output}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const CHECK_STATUS_STYLES: Record<string, string> = {
  pass:    "bg-green-100 text-green-800",
  warning: "bg-amber-100 text-amber-800",
  fail:    "bg-red-100 text-red-800",
}

const CHECK_STATUS_LABELS: Record<string, string> = {
  pass:    "Pass",
  warning: "Warning",
  fail:    "Fail",
}

const DEFAULT_CHECKS = [
  "Title tags",
  "Meta descriptions",
  "Image alt text",
  "Broken links",
  "Page speed",
  "Mobile friendliness",
  "Structured data",
  "Canonical URLs",
]

function CheckBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CHECK_STATUS_STYLES[status] ?? "bg-stone-100 text-stone-600"}`}>
      {CHECK_STATUS_LABELS[status] ?? status}
    </span>
  )
}

function ScoreTile({
  label,
  score,
}: {
  label: string
  score: number | null
}) {
  const color =
    score === null
      ? "#9CA3AF"
      : score >= 90
      ? "#7BA696"
      : score >= 50
      ? "#D97706"
      : "#EF4444"

  return (
    <div className="flex flex-col items-center rounded-lg border border-hairline bg-surface p-5 gap-2">
      <div
        className="flex h-16 w-16 items-center justify-center rounded-full text-xl font-semibold text-white"
        style={{ backgroundColor: color }}
      >
        {score ?? "—"}
      </div>
      <span className="text-sm text-ink-muted text-center">{label}</span>
    </div>
  )
}

function SuggestionCard({ suggestion }: { suggestion: Suggestion }) {
  function act(action: "accept" | "dismiss") {
    router.patch(`/admin/seo/suggestions/${suggestion.id}/${action}`, {}, { preserveScroll: true })
  }

  const evidenceQueries = suggestion.evidence?.queries ?? []

  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="muted">{suggestion.brand_name}</Badge>
        <span className="text-sm font-medium text-ink-display">
          {suggestion.page_label}
          {suggestion.section_label ? ` — ${suggestion.section_label}` : " — Metadata"}
        </span>
        {suggestion.level && <Badge tone="neutral">{suggestion.level}</Badge>}
        {suggestion.status === "accepted" && <Badge tone="accent">Queued for tonight's sync</Badge>}
      </div>

      <p className="mt-2 text-sm text-ink-body">{suggestion.summary}</p>
      {suggestion.rationale && <p className="mt-1 text-sm text-ink-muted">{suggestion.rationale}</p>}

      <div className="mt-2 flex flex-wrap gap-1.5">
        {suggestion.target_phrases.map((phrase) => (
          <Badge key={phrase} tone="neutral">
            {phrase}
          </Badge>
        ))}
      </div>

      {evidenceQueries.length > 0 && (
        <p className="mt-2 text-xs text-ink-muted">
          {evidenceQueries
            .map((q) =>
              [
                `"${q.query}"`,
                q.impressions ? `${q.impressions} impressions` : null,
                q.best_position ? `position ${q.best_position}` : null,
              ]
                .filter(Boolean)
                .join(" · "),
            )
            .join(" — ")}
        </p>
      )}

      {suggestion.status === "pending" && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={() => act("accept")}>
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            Accept — rewrite tonight
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => act("dismiss")}>
            Dismiss
          </Button>
        </div>
      )}
    </li>
  )
}

export default function AdminSeo() {
  const { props } = usePage<Props>()
  const { latest_report, recent_reports, suggestions, search_terms_summary, agent, flash } = props

  function handleRunNow() {
    router.post("/admin/seo/run-now")
  }

  const checks = latest_report?.checks?.length
    ? latest_report.checks
    : DEFAULT_CHECKS.map((name) => ({ name, status: "pending" as const }))

  return (
    <AdminShell>
      <Head title="SEO">
        <meta name="description" content="SEO audit dashboard for Sanctuary Therapy Centre." />
        <meta property="og:title" content="SEO" />
        <meta property="og:description" content="SEO audit dashboard for Sanctuary Therapy Centre." />
      </Head>
      <PageHeader
        title="SEO"
        description="Audit scores and health checks from the SEO agent."
        actions={
          <Button onClick={handleRunNow} disabled={!agent.configured || agent.current !== null}>
            <Sparkles className="h-4 w-4" />
            <span className="ml-2">{agent.current ? "Agent running…" : "Run SEO agent now"}</span>
          </Button>
        }
      />

      {flash.notice && (
        <div className="mt-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {flash.notice}
        </div>
      )}
      {flash.alert && <p className="mt-4 text-sm text-danger">{flash.alert}</p>}

      <AgentPanel agent={agent} />

      {/* Weekly wording suggestions */}
      <div className="mt-8 rounded-lg border border-hairline bg-surface">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline px-5 py-3">
          <div>
            <h2>Wording suggestions</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              What people search for vs. what the pages say — reviewed weekly by the SEO agent.
              Accepting a suggestion queues an AI rewrite for tonight&apos;s sync; nothing changes
              without your say-so.
            </p>
          </div>
          <p className="text-xs text-ink-muted">
            {search_terms_summary.last_pull_week
              ? `Search data: week of ${search_terms_summary.last_pull_week} — ${search_terms_summary.gsc_terms} Search Console, ${search_terms_summary.autocomplete_terms} autocomplete`
              : "No search data pulled yet."}
            {!search_terms_summary.gsc_configured && " · Search Console not connected (set GSC_CREDENTIALS)"}
          </p>
        </div>
        {suggestions.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-ink-muted">
            No open suggestions — the weekly agent (bin/seo-suggest) files them here.
          </p>
        ) : (
          <ul className="divide-y divide-hairline">
            {suggestions.map((s) => (
              <SuggestionCard key={s.id} suggestion={s} />
            ))}
          </ul>
        )}
      </div>

      {/* Score overview */}
      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <ScoreTile label="Performance" score={latest_report?.performance_score ?? null} />
        <ScoreTile label="SEO" score={latest_report?.seo_score ?? null} />
        <ScoreTile label="Accessibility" score={latest_report?.accessibility_score ?? null} />
        <ScoreTile label="Best Practices" score={latest_report?.best_practices_score ?? null} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-5">
        {/* Health checks */}
        <div className="lg:col-span-3">
          <div className="rounded-lg border border-hairline bg-surface">
            <div className="border-b border-hairline px-5 py-3">
              <h2>Health checks</h2>
              {!latest_report && (
                <p className="mt-0.5 text-sm text-ink-muted">No audit data yet — checks shown as pending.</p>
              )}
            </div>
            <ul className="divide-y divide-hairline">
              {checks.map((check) => (
                <li key={check.name} className="flex items-center justify-between px-5 py-3">
                  <span className="text-sm text-ink-body">{check.name}</span>
                  <CheckBadge status={check.status} />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Next scheduled audit */}
          <div className="rounded-lg border border-hairline bg-surface p-5">
            <h2 className="mb-2">Next scheduled audit</h2>
            {latest_report?.next_run_at ? (
              <p className="text-ink-body">
                {new Date(latest_report.next_run_at).toLocaleDateString("en-GB", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            ) : (
              <p className="text-ink-muted text-sm">Not yet scheduled.</p>
            )}
          </div>

          {/* Audit history */}
          <div className="rounded-lg border border-hairline bg-surface">
            <div className="border-b border-hairline px-5 py-3">
              <h2>Audit history</h2>
            </div>
            {recent_reports.length === 0 ? (
              <p className="px-5 py-6 text-center text-sm text-ink-muted">No audits yet.</p>
            ) : (
              <ul className="divide-y divide-hairline">
                {recent_reports.map((r) => (
                  <li key={r.id} className="px-5 py-3">
                    <p className="text-xs text-ink-muted">
                      {new Date(r.created_at).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-3 text-sm">
                      <span className="text-ink-muted">SEO <strong>{r.seo_score}</strong></span>
                      <span className="text-ink-muted">Perf <strong>{r.performance_score}</strong></span>
                      <span className="text-ink-muted">A11y <strong>{r.accessibility_score}</strong></span>
                    </div>
                    {r.summary && (
                      <p className="mt-1 text-xs text-ink-muted line-clamp-2">{r.summary}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  )
}
